import Cookies from 'js-cookie'
import { isAnalyticsAllowed } from './consentManager'
import { getReferrerData, REFERRER_ATTR } from './trafficSource'

const COOKIE_NAME = 'th_attribution'
const TEMP_STORAGE_KEY = `${COOKIE_NAME}_temp`
const CONFIG = {
  path: '/',
  expires: 365,
  sameSite: 'Lax',
  secure: window.location.protocol === 'https:',
}
const RESERVED_ATTR = ['tracking_created_at', 'tracking_updated_at', 'landing_page', ...REFERRER_ATTR]
const MAX_PARAM_LENGTH = 200

export function setAttributionCookie(data) {
  try {
    if (!isAnalyticsAllowed()) {
      return false
    }

    const cookieData = {
      tracking_created_at: new Date().toISOString(),
      tracking_updated_at: new Date().toISOString(),
      landing_page: window.location.pathname,
      ...data,
    }

    Cookies.set(COOKIE_NAME, JSON.stringify(cookieData), CONFIG)
    return true
  } catch (error) {
    console.warn('Attribution tracking failed:', error)
    return false
  }
}

export function getAttributionData() {
  try {
    if (!isAnalyticsAllowed()) {
      return null
    }

    const cookieValue = Cookies.get(COOKIE_NAME)
    if (!cookieValue) {
      return null
    }

    return JSON.parse(cookieValue)
  } catch (error) {
    console.warn('Failed to get attribution data:', error)
    return null
  }
}

export function capturePreConsentData() {
  const urlParams = getUrlParameters()
  const referrerData = getReferrerDataSafely()

  const capturedData = {
    ...urlParams,
    ...referrerData,
    tracking_created_at: new Date().toISOString(),
    landing_page: window.location.pathname,
  }

  storeTempData(capturedData)
}

export function handleAttributionCookies() {
  if (!isAnalyticsAllowed()) {
    return
  }

  const existingData = getAttributionData()
  const tempData = retrieveAndClearTempData()

  if (Object.keys(tempData).length > 0 || !existingData?.referrer_type) {
    const currentParams = getUrlParameters()
    const currentReferrer = getReferrerDataSafely()

    // Oldest properties take precedence, only write from new data if previous value is empty
    // params are the exception
    const mergedData = {
      ...currentReferrer,
      ...tempData,
      ...existingData,
      ...currentParams,
    }
    setAttributionCookie(mergedData)
  }
}

function getUrlParameters() {
  const urlParams = new URLSearchParams(window.location.search)
  const safeUrlParams = [...urlParams].filter(([key, value]) => {
    if (!value) return false

    if (RESERVED_ATTR.includes(key)) return false

    if (value.length > MAX_PARAM_LENGTH) return false

    if (containsXSS(value)) return false

    return true
  })

  return Object.fromEntries(safeUrlParams)
}

function containsXSS(value) {
  const scriptTags = /(<|%3C|&lt;|&#60;|&#x3C;)/i

  const dangerousPatterns = /(\b(javascript|data|vbscript|file):|on\w+\s*=)/i

  return scriptTags.test(value) || dangerousPatterns.test(value)
}

function getReferrerDataSafely() {
  try {
    return getReferrerData()
  } catch (error) {
    console.warn('Failed to get referrer data:', error)
    return { referrer_type: 'unknown' }
  }
}

function storeTempData(data) {
  try {
    sessionStorage.setItem(TEMP_STORAGE_KEY, JSON.stringify(data))
  } catch (error) {
    console.warn('Failed to store temp attribution data:', error)
  }
}

function retrieveAndClearTempData() {
  try {
    const tempData = sessionStorage.getItem(TEMP_STORAGE_KEY)
    if (tempData) {
      sessionStorage.removeItem(TEMP_STORAGE_KEY)
      return JSON.parse(tempData)
    }
  } catch (error) {
    console.warn('Failed to retrieve temp attribution data:', error)
  }

  return {}
}
